import { useEffect } from 'react'
import moment from 'moment'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  fetchSubscriptions,
  selectHeardProductSubscription,
  selectSubscription,
  SUBSCRIPTION_DEFAULT_KEY,
} from '@/reducers/subscription.slice'
import { TrialStatus } from '@/reducers/auth/userReducer'

import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import {
  getFreeTrialEndsAt,
  getFreeTrialStatus,
  hasManualRequest,
  isCancelling,
  isFreeTrialPromoCode,
} from '@/selectors/user.selectors'

import { centsToCurrency } from '@/utils/currencyHelpers'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import CurrencyFormatLabel from '@/shared/CurrencyFormatLabel'
import { useAnalyticsView } from '@/features/amplitude'
import { fetchPayrollProfileIfNeeded } from '@/featuresV1/Payroll/payrollActions'
import { fetchUserTransactions } from '@/features/transactions/transactions.slice'
import { fetchUserTaxEstimates } from '@/featuresV1/Taxes/QuarterlyTaxEstimates/userTaxEstimates.slice'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { fetchAnnualTaxFilingFormsIfNeeded } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilingForms.slice'

import { TypographyStyles } from '@/styles'
import { Alert, Icon, Table, Text } from '@/ui'

export const BillingTable = () => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const subscription = useReselector(selectSubscription)
  const primarySubscription = useReselector(selectHeardProductSubscription)
  const membershipIsCancelling = useReselector(isCancelling)
  const membershipHasManualRequest = useReselector(hasManualRequest)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)
  const trialEndsAt = useReselector(getFreeTrialEndsAt)

  const isInActiveFreeTrial =
    hasFreeTrialPromoCode && userFreeTrialStatus === TrialStatus.active

  useEffect(() => {
    pageView('plans and billing page')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchSubscriptions())
    dispatch(fetchPayrollProfileIfNeeded())
    dispatch(fetchUserTransactions())
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchCurrentUserDetailsIfNeeded(true))
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
  }, [dispatch])

  return (
    <Table>
      {!isMobile && (
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Plan</Table.HeaderCell>
            <Table.HeaderCell>Price</Table.HeaderCell>
            <Table.HeaderCell>Billing Start Date</Table.HeaderCell>
            <Table.HeaderCell>Next Billing Date</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
      )}

      {isInActiveFreeTrial && (
        <Table.Body>
          <Table.Row>
            <Table.Cell colSpan={4}>
              <Alert>
                <Text variant={TypographyStyles.BODY_LG}>
                  {' '}
                  You are previewing the Heard Experience, with{' '}
                  {moment(trialEndsAt).diff(moment(), 'days')} days remaining in
                  your trial
                </Text>
              </Alert>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      )}

      {!isInActiveFreeTrial && (
        <Table.Body>
          {Object.values(subscription).map((sub) => (
            <Table.Row key={sub.id}>
              <Table.Cell>{sub.name}</Table.Cell>
              <Table.Cell>
                {Object.values(sub.items).map((item) => (
                  <p key={item.id}>
                    {item.name !== SUBSCRIPTION_DEFAULT_KEY && (
                      <>
                        {item.name}
                        {item.quantity > 1 && (
                          <>&nbsp;({item.quantity} total)</>
                        )}
                        :&nbsp;
                      </>
                    )}
                    <Text>
                      <CurrencyFormatLabel
                        value={
                          centsToCurrency(item.amountInCents).multiply(
                            item.quantity
                          ).value
                        }
                      />
                    </Text>
                  </p>
                ))}
              </Table.Cell>

              {isMobile && <Table.Cell />}

              <Table.Cell>
                {isMobile && (
                  <Text variant={TypographyStyles.BODY_XS}>
                    BILLING START DATE
                  </Text>
                )}
                {moment.unix(sub.startDate).format(DATE_FORMATS.DISPLAY_LONG)}
              </Table.Cell>

              <Table.Cell>
                {isMobile && (
                  <Text variant={TypographyStyles.BODY_XS}>
                    NEXT BILLING DATE
                  </Text>
                )}
                {moment
                  .unix(sub.nextBillingDate)
                  .format(DATE_FORMATS.DISPLAY_LONG)}
              </Table.Cell>
            </Table.Row>
          ))}

          {membershipIsCancelling && (
            <Table.Row>
              <Table.Cell>
                <Icon icon={regular('triangle-exclamation')} />

                <Text className="text-danger">
                  Subscription Ending on:{' '}
                  {primarySubscription?.nextBillingDate &&
                    moment
                      .unix(primarySubscription.nextBillingDate)
                      .format(DATE_FORMATS.DISPLAY_LONG)}
                </Text>
              </Table.Cell>
            </Table.Row>
          )}

          {membershipHasManualRequest && (
            <Table.Row>
              <Table.Cell>
                <Icon icon={regular('triangle-exclamation')} />

                <Text className="text-danger">
                  You&#39;ve requested the cancellation of your Heard plan.
                  We&#39;ll get back to you as soon as we can.
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      )}
    </Table>
  )
}
